/**
 * Which campaign statuses count as "has run or is running".
 *
 * Three pages had each grown their own copy of this: the Analytics picker, the two
 * backend analytics endpoints, and Profile's Active Campaigns card. Profile's was the
 * one nobody noticed, so drafts appeared there under a heading saying Active.
 *
 * Kept in step with REPORTABLE_STATUSES in backend/campaign_rules.py. If these drift,
 * a campaign is reportable on one screen and not another.
 */
export const REPORTABLE_STATUSES = ["ACTIVE", "PAUSED", "COMPLETED", "EXPIRED"];

/** True when a campaign has run or is running, so it belongs in a report. */
export function isReportable(campaign) {
  return REPORTABLE_STATUSES.includes((campaign || {}).status);
}

/**
 * What an offer's lifecycle badge should say, from the campaign that owns it.
 *
 * The backend derives `offer.status` as ACTIVE or EXPIRED and nothing else, because
 * serving only needs to know whether to deliver. That is wrong for any page a merchant
 * reads, twice over:
 *
 *   DRAFT   resolves to EXPIRED, so offers a merchant has just created in a draft
 *           campaign are dimmed and badged Expired (round 7, SP-32).
 *   PAUSED  resolves to EXPIRED, reading as lost rather than resumable.
 *
 * Both are answered from the RAW campaign_status, which the offers endpoints already
 * return alongside the derived status. A null campaign_status means the campaign has
 * been archived - the document is deleted and the offer outlives it by design - so that
 * genuinely is expired.
 */
export function offerLifecycle(offer) {
  const o = offer || {};
  switch (o.campaign_status) {
    case "DRAFT":
      return { key: "DRAFT", label: "Draft", expired: false, dim: false };
    case "PAUSED":
      return { key: "PAUSED", label: "Paused", expired: false, dim: true };
    case "ACTIVE":
      return { key: "ACTIVE", label: null, expired: false, dim: false };
    default:
      // COMPLETED, EXPIRED, or archived (campaign_status absent). Fall back to the
      // derived status so an offer whose campaign is gone still reads correctly.
      return o.status === "EXPIRED"
        ? { key: "EXPIRED", label: "Expired", expired: true, dim: true }
        : { key: "ACTIVE", label: null, expired: false, dim: false };
  }
}
